import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';

import { Button, Drawer, DrawerFooter, DrawerHeader, focusRing, IconButton } from '@/design-system';
import type { PublicReportPhoto } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';

export interface ReportPhotosProps {
    photos: PublicReportPhoto[];
    copy: PublicCopy;
    /** De huisstijl van het bedrijf, voor het venster dat buiten de pagina opent. */
    brandStyle: CSSProperties;
    /**
     * Een foto laadt niet. Fotolinks zijn ondertekend en verlopen na een kwartier,
     * en wie lang leest, scrolt pas later naar een foto: dan zijn verse links nodig.
     */
    onLinkExpired?: () => void;
}

/**
 * De foto's bij een bevinding: klein in de tekst, zoals in de PDF, en groot in
 * een lichtbak na een tik. De kleine laden pas als ze bijna in beeld komen, zodat
 * een lang rapport met tachtig foto's snel opent.
 */
export function ReportPhotos({ photos, copy, brandStyle, onLinkExpired }: ReportPhotosProps) {
    const [open, setOpen] = useState<number | null>(null);

    if (photos.length === 0) {
        return null;
    }

    return (
        <>
            <ul className="m-0 mt-3 grid list-none gap-3 p-0 [grid-template-columns:repeat(auto-fill,minmax(136px,1fr))]">
                {photos.map((photo, index) => (
                    <li key={index} className="m-0 min-w-0">
                        <button
                            type="button"
                            aria-label={copy.photoOpen(photo.caption, index + 1, photos.length)}
                            onClick={() => setOpen(index)}
                            className={cn(
                                'block w-full cursor-zoom-in rounded-[8px] border-0 bg-transparent p-0',
                                focusRing,
                            )}
                        >
                            <img
                                src={photo.thumbUrl}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                onError={onLinkExpired}
                                className="block aspect-[4/3] w-full rounded-[8px] border border-[var(--placeholder-border)] bg-[var(--surface-subtle)] object-cover"
                            />
                        </button>
                        {photo.caption ? (
                            <p className="m-0 mt-[6px] text-[12px] leading-[1.4] break-words text-[var(--text-tertiary-safe)]">
                                {photo.caption}
                            </p>
                        ) : null}
                    </li>
                ))}
            </ul>

            {open !== null && photos[open] ? (
                <PhotoViewer
                    photos={photos}
                    index={open}
                    onIndex={setOpen}
                    copy={copy}
                    brandStyle={brandStyle}
                    {...(onLinkExpired ? { onLinkExpired } : {})}
                />
            ) : null}
        </>
    );
}

/** Zo ver moet een vinger opzij, en meer opzij dan omhoog, om een veeg te zijn. */
const SWIPE_DISTANCE = 48;

/**
 * De lichtbak: één foto groot op een donkere achtergrond, met de volgende en
 * vorige onder de pijltjes van het toetsenbord, de knoppen onderaan en een veeg
 * naar links of rechts. Home en End gaan naar de eerste en de laatste.
 *
 * Een `Drawer` over de hele breedte van een telefoon: die houdt de focus vast
 * (Tab blijft erin), sluit met Escape of de sluitknop, en geeft de focus terug
 * aan de foto die hem opende. Een veeg omhoog of omlaag blijft scrollen, en
 * knijpen blijft zoomen.
 */
function PhotoViewer({
    photos,
    index,
    onIndex,
    copy,
    brandStyle,
    onLinkExpired,
}: {
    photos: PublicReportPhoto[];
    index: number;
    onIndex: (index: number | null) => void;
    copy: PublicCopy;
    brandStyle: CSSProperties;
    onLinkExpired?: () => void;
}) {
    const photo = photos[index];
    const count = photos.length;
    const close = () => onIndex(null);
    const step = (by: number) => onIndex((index + by + count) % count);
    const swipe = useRef<{ x: number; y: number; pointer: number } | null>(null);

    if (!photo) {
        return null;
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (count < 2) {
            return;
        }

        const target =
            event.key === 'ArrowRight'
                ? (index + 1) % count
                : event.key === 'ArrowLeft'
                  ? (index - 1 + count) % count
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? count - 1
                      : null;

        if (target !== null) {
            event.preventDefault();
            onIndex(target);
        }
    };

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }

        swipe.current = { x: event.clientX, y: event.clientY, pointer: event.pointerId };
    };

    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        const start = swipe.current;

        swipe.current = null;

        if (!start || start.pointer !== event.pointerId || count < 2) {
            return;
        }

        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;

        if (Math.abs(dx) >= SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * 1.5) {
            step(dx < 0 ? 1 : -1);
        }
    };

    return (
        <Drawer
            open
            onClose={close}
            side="right"
            width={960}
            title={photo.caption ?? copy.photoTitle}
            style={brandStyle}
            className="max-w-full"
            onKeyDown={onKeyDown}
        >
            <DrawerHeader
                title={photo.caption ?? copy.photoTitle}
                {...(count > 1 ? { eyebrow: copy.photoPosition(index + 1, count) } : {})}
                onClose={close}
                closeLabel={copy.photoClose}
            />

            <div
                data-photo-stage=""
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={() => {
                    swipe.current = null;
                }}
                className="relative flex flex-1 touch-pan-y touch-pinch-zoom items-center justify-center bg-[var(--ink-900)] p-4 select-none"
            >
                <img
                    key={photo.url}
                    src={photo.url}
                    alt={photo.caption ?? ''}
                    decoding="async"
                    draggable={false}
                    onError={onLinkExpired}
                    className="block h-auto max-h-[calc(100dvh-190px)] max-w-full rounded-[6px] object-contain"
                />

                {count > 1 ? (
                    <>
                        {/*
                         * Breed staan vorige en volgende ook naast de foto, voor de muis. Voor
                         * toetsenbord en schermlezer zijn er de pijltjestoetsen en de knoppen
                         * onderaan; deze twee tellen daar niet nog eens mee.
                         */}
                        <IconButton
                            icon="back"
                            variant="onDark"
                            size="md"
                            aria-label={copy.photoPrevious}
                            aria-hidden="true"
                            tabIndex={-1}
                            onClick={() => step(-1)}
                            className="absolute top-1/2 left-3 hidden -translate-y-1/2 @min-[700px]:inline-flex"
                        />
                        <IconButton
                            icon="back"
                            variant="onDark"
                            size="md"
                            aria-label={copy.photoNext}
                            aria-hidden="true"
                            tabIndex={-1}
                            onClick={() => step(1)}
                            className="absolute top-1/2 right-3 hidden -translate-y-1/2 @min-[700px]:inline-flex [&_svg]:rotate-180"
                        />
                    </>
                ) : null}
            </div>

            {count > 1 ? (
                <DrawerFooter className="justify-between">
                    <Button variant="outline" size="md" onClick={() => step(-1)}>
                        {copy.photoPrevious}
                    </Button>
                    <Button variant="outline" size="md" onClick={() => step(1)}>
                        {copy.photoNext}
                    </Button>
                </DrawerFooter>
            ) : null}
        </Drawer>
    );
}
